"use client"

import * as React from "react"
import { Puzzle, Pencil, Trash2, ShoppingBag } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import { toast } from "@/lib/toast"

export interface WidgetsSettingsTabProps {
  availableWidgets: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  handleOpenMarketplaceTab?: () => void
  setSettingsTab: (tab: string) => void
  isCompanyAdmin?: boolean
}

export function WidgetsSettingsTab({
  availableWidgets,
  setWidgetsConfig,
  handleOpenMarketplaceTab,
  setSettingsTab,
  isCompanyAdmin = true,
}: WidgetsSettingsTabProps) {
  const [showNewWidgetModal, setShowNewWidgetModal] = React.useState(false)
  const [editingWidget, setEditingWidget] = React.useState<any>(null)
  const [newWidgetName, setNewWidgetName] = React.useState("")
  const [newWidgetDescription, setNewWidgetDescription] = React.useState("")
  const [newWidgetIcon, setNewWidgetIcon] = React.useState("🧮")
  const [newWidgetDimension, setNewWidgetDimension] = React.useState<"small" | "large">("small")
  const [newWidgetDevType, setNewWidgetDevType] = React.useState<"iframe" | "code">("iframe")
  const [newWidgetIframeUrl, setNewWidgetIframeUrl] = React.useState("")
  const [newWidgetCode, setNewWidgetCode] = React.useState("")

  return (
    <TabsContent value="widgets-list-tab" className="outline-none space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-row items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
            <Puzzle className="size-5 text-[#004EB1]" />
            Gerenciamento de Widgets
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            Gerencie e ative os widgets oficiais ou crie seus próprios micro-apps customizados.
          </p>
        </div>

        {/* Botão de Adicionar Aplicativo/Widget (Apenas Owner/Admin da Empresa) - Direciona para o Marketplace */}
        {isCompanyAdmin && (
          <Button
            size="sm"
            onClick={() => {
              if (handleOpenMarketplaceTab) {
                handleOpenMarketplaceTab()
              } else {
                setSettingsTab("marketplace")
              }
            }}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-3 flex items-center gap-1.5 cursor-pointer font-sans shadow-sm"
          >
            <ShoppingBag className="size-3.5" />
            <span>Loja de Aplicativos</span>
          </Button>
        )}
      </div>

      {/* Formulário de Criação / Edição de Widget */}
      {(showNewWidgetModal || editingWidget) && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (editingWidget) {
              setWidgetsConfig?.((prev) =>
                prev.map((w) =>
                  w.id === editingWidget.id
                    ? {
                      ...w,
                      name: newWidgetName,
                      description: newWidgetDescription,
                      icon: newWidgetIcon,
                      dimension: newWidgetDimension,
                      developerType: newWidgetDevType,
                      iframeUrl: newWidgetIframeUrl,
                      code: newWidgetCode,
                    }
                    : w
                )
              )
              toast.success("Widget Atualizado", { description: "As alterações foram salvas." })
            } else {
              const newId = `custom-widget-${Date.now()}`
              const newW = {
                id: newId,
                name: newWidgetName,
                description: newWidgetDescription,
                icon: newWidgetIcon || "🧩",
                type: "developer",
                enabled: true,
                visible: true,
                dimension: newWidgetDimension,
                developerType: newWidgetDevType,
                iframeUrl: newWidgetIframeUrl,
                code: newWidgetCode,
                defaultWidth: newWidgetDimension === "large" ? 480 : 320,
                defaultHeight: newWidgetDimension === "large" ? 430 : 380,
              }
              setWidgetsConfig?.((prev) => [...prev, newW])
              toast.success("Widget Criado", { description: "Seu novo widget já está disponível." })
            }
            setShowNewWidgetModal(false)
            setEditingWidget(null)
          }}
          className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 bg-zinc-50 dark:bg-zinc-950/50 space-y-4 animate-in fade-in duration-200"
        >
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-zinc-850 dark:text-zinc-200 font-sans">
              {editingWidget ? "Editar Widget Customizado" : "Registrar Novo Widget Customizado"}
            </h4>
            <button
              type="button"
              onClick={() => {
                setShowNewWidgetModal(false)
                setEditingWidget(null)
              }}
              className="text-xs text-zinc-455 hover:text-zinc-700 dark:hover:text-zinc-200 cursor-pointer font-sans font-semibold"
            >
              Cancelar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                Nome do Widget
              </label>
              <input
                type="text"
                placeholder="Ex: Minhas Vendas"
                value={newWidgetName}
                onChange={(e) => setNewWidgetName(e.target.value)}
                className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                  Ícone (Emoji)
                </label>
                <input
                  type="text"
                  placeholder="Emoji ou Letra"
                  value={newWidgetIcon}
                  onChange={(e) => setNewWidgetIcon(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans text-center"
                  maxLength={2}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                  Dimensão
                </label>
                <select
                  value={newWidgetDimension}
                  onChange={(e) => setNewWidgetDimension(e.target.value as any)}
                  className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans cursor-pointer"
                >
                  <option value="small">Pequeno (1x1)</option>
                  <option value="large">Grande (2x1)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
              Descrição
            </label>
            <textarea
              placeholder="Descreva brevemente o propósito deste micro-app..."
              value={newWidgetDescription}
              onChange={(e) => setNewWidgetDescription(e.target.value)}
              className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans h-16 resize-none"
              required
            />
          </div>

          <div className="space-y-3 pt-2 border-t border-zinc-200/60 dark:border-zinc-800/60">
            <div className="flex gap-4 items-center">
              <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                Tipo de Integração
              </label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-zinc-700 dark:text-zinc-300 font-sans cursor-pointer">
                  <input
                    type="radio"
                    name="widgetType"
                    value="iframe"
                    checked={newWidgetDevType === "iframe"}
                    onChange={(e) => setNewWidgetDevType(e.target.value as any)}
                    className="cursor-pointer"
                  />
                  Iframe (URL Externa)
                </label>
                <label className="flex items-center gap-1.5 text-xs text-zinc-700 dark:text-zinc-300 font-sans cursor-pointer">
                  <input
                    type="radio"
                    name="widgetType"
                    value="code"
                    checked={newWidgetDevType === "code"}
                    onChange={(e) => {
                      setNewWidgetDevType(e.target.value as any)
                      if (e.target.value === "code" && !newWidgetCode) {
                        setNewWidgetCode(
                          `<!-- Modelo de Micro-app: Contador Interativo -->\n<div style="text-align: center; padding: 16px; font-family: inherit;">\n  <h3 style="margin: 0; font-size: 14px; font-weight: 600;">Contador</h3>\n  <p style="font-size: 24px; font-weight: 300; margin: 8px 0;" id="counter-val">0</p>\n  <div style="display: flex; gap: 8px; justify-content: center;">\n    <button onclick="update(-1)">-</button>\n    <button onclick="update(1)">+</button>\n  </div>\n</div>\n<script>\n  let count = 0;\n  function update(val) {\n    count += val;\n    document.getElementById('counter-val').innerText = count;\n  }\n</script>`
                        )
                      }
                    }}
                    className="cursor-pointer"
                  />
                  Código Customizado (HTML/JS)
                </label>
              </div>
            </div>

            {newWidgetDevType === "iframe" ? (
              <div className="space-y-1.5 animate-in fade-in duration-200">
                <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                  URL do Iframe
                </label>
                <input
                  type="url"
                  placeholder="https://exemplo.com/meu-widget"
                  value={newWidgetIframeUrl}
                  onChange={(e) => setNewWidgetIframeUrl(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans"
                  required={newWidgetDevType === "iframe"}
                />
              </div>
            ) : (
              <div className="space-y-1.5 animate-in fade-in duration-200">
                <div className="flex justify-between items-center">
                  <label className="text-[11px] font-bold text-zinc-550 dark:text-zinc-400 font-sans">
                    Código HTML, CSS e JavaScript
                  </label>
                </div>
                <textarea
                  value={newWidgetCode}
                  onChange={(e) => setNewWidgetCode(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-mono h-48"
                  required={newWidgetDevType === "code"}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200/50 dark:border-zinc-800/40">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowNewWidgetModal(false)
                setEditingWidget(null)
              }}
              className="text-xs h-8 cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white font-semibold text-xs cursor-pointer"
            >
              {editingWidget ? "Salvar Alterações" : "Criar Widget"}
            </Button>
          </div>
        </form>
      )}

      {/* Card Único: Listagem de Widgets (Oficiais + Customizados) */}
      <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <CardHeader>
          <CardTitle className="text-sm font-bold text-zinc-850 dark:text-zinc-100 font-sans">
            Widgets Habilitados no Workspace
          </CardTitle>
          <CardDescription className="text-xs font-sans">
            Ative ou desative a exibição dos widgets no painel lateral de notificações.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-zinc-100 dark:divide-zinc-800/60 p-0">
          {availableWidgets.map((widget) => {
            const isDefault = widget.isDefault || ["system-calendar", "system-weather", "system-calculator", "system-notes"].includes(widget.id)
            const isSystem = widget.type === "system" || widget.type === "official"
            return (
              <div
                key={widget.id}
                className="flex items-center justify-between p-4 hover:bg-zinc-50/30 dark:hover:bg-zinc-950/10 transition-colors"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="size-9 rounded-xl border border-zinc-200/60 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-850 flex items-center justify-center shrink-0">
                    <WidgetIcon id={widget.id} icon={widget.icon} name={widget.name} className="size-5 text-[#004EB1] dark:text-blue-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 font-sans truncate">
                        {widget.name}
                      </h4>
                      <span
                        className={`text-[8px] font-extrabold px-1.5 py-0.5 rounded-full border uppercase tracking-wider shrink-0 ${isDefault
                            ? "bg-blue-50 border-blue-200 text-[#004EB1] dark:bg-blue-950/20 dark:border-blue-900/30 dark:text-blue-400"
                            : isSystem
                              ? "bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-900/30 dark:text-emerald-400"
                              : "bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-950/20 dark:border-purple-900/30 dark:text-purple-400"
                          }`}
                      >
                        {isDefault ? "Oficial Bold" : isSystem ? "Oficial Bold" : "Customizado"}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5 font-sans leading-relaxed line-clamp-2">
                      {widget.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0 ml-4">
                  {/* Botoes de Editar e Deletar apenas para Widgets Customizados e não-padrão (Exclusivo Admins da Empresa) */}
                  {!isDefault && !isSystem && isCompanyAdmin && (
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingWidget(widget)
                          setNewWidgetName(widget.name)
                          setNewWidgetDescription(widget.description || "")
                          setNewWidgetIcon(widget.icon || "🧩")
                          setNewWidgetDimension(widget.dimension || "small")
                          setNewWidgetDevType(widget.developerType || "iframe")
                          setNewWidgetIframeUrl(widget.iframeUrl || "")
                          setNewWidgetCode(widget.code || "")
                        }}
                        className="text-blue-550 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-955/20 size-7 cursor-pointer rounded-lg"
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setWidgetsConfig?.((prev) => prev.filter((w) => w.id !== widget.id))
                          toast.success("Widget Removido", { description: `${widget.name} foi excluído.` })
                        }}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-955/20 size-7 cursor-pointer rounded-lg"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  )}

                  {/* Switch de Ativar / Desativar apenas para Widgets não-padrão */}
                  {!isDefault && (
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold ${widget.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400 dark:text-zinc-500"}`}>
                        {widget.enabled ? "Ativo" : "Inativo"}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setWidgetsConfig?.((prev) =>
                            prev.map((w) => (w.id === widget.id ? { ...w, enabled: !w.enabled } : w))
                          )
                        }}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${widget.enabled ? "bg-[#004EB1]" : "bg-zinc-200 dark:bg-zinc-850"
                          }`}
                        title={widget.enabled ? "Desativar widget" : "Ativar widget"}
                      >
                        <span
                          className={`pointer-events-none inline-block size-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${widget.enabled ? "translate-x-4" : "translate-x-0"
                            }`}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
    </TabsContent>
  )
}
